import React from "react";
import GarmentThumb from "./GarmentThumb.jsx";

// Normalised to lowercase to match garment.type values (DB stores lowercase)
const GARMENT_PRIORITY = ["shoes", "pants", "shirt", "sweater", "jacket", "coat", "belt"];

/**
 * Garment selection grid with type-filter tabs.
 * Computes activeGarments / garmentTypes / visible internally.
 */
export default function GarmentPicker({
  garments, selected, toggleGarment, onClearAll,
  filter, setFilter,
  isDark, card, border, muted,
}) {
  const { useState, useEffect } = React;
  const [collapsed, setCollapsed] = useState(false);
  // Auto-expand when items already selected (restoring today's entry or async IDB load)
  useEffect(() => { if (selected.size > 0) setCollapsed(false); }, [selected.size]);

  const activeGarments = garments.filter(
    g => !g.excludeFromWardrobe && g.type !== "outfit-photo" && g.type !== "outfit-shot"
  );

  const types = new Set(activeGarments.map(g => (g.type ?? "").toLowerCase()).filter(Boolean));
  const garmentTypes = [
    "all",
    ...GARMENT_PRIORITY.filter(t => types.has(t)),
    ...[...types].filter(t => !GARMENT_PRIORITY.includes(t)),
  ];

  const visible = (filter === "all"
    ? activeGarments
    : activeGarments.filter(g => (g.type ?? "").toLowerCase() === filter)
  ).slice().sort((a, b) => {
    // Sort by recency-of-wear first (most recent at top), then by total wear count,
    // so daily drivers (e.g. Ecco S-Lite, RL light blue shirt) float above visually-
    // similar duplicates during check-in. Items never worn (no lastWorn) sink to the
    // bottom; among never-worn, preserve natural order. Garment with same lastWorn
    // date is tie-broken by wearCount (more-worn first), then natural order.
    const aWorn = a.lastWorn || "";
    const bWorn = b.lastWorn || "";
    if (aWorn !== bWorn) return bWorn.localeCompare(aWorn); // ISO date strings sort lex
    const aCount = a.wearCount ?? 0;
    const bCount = b.wearCount ?? 0;
    if (aCount !== bCount) return bCount - aCount;
    return 0; // stable: preserve natural order on full tie
  });

  return (
    <div style={{ background: card, borderRadius: 14, border: `1px solid ${border}`, padding: collapsed ? "10px 16px" : 16, marginBottom: 14 }}>
      <div
        onClick={() => setCollapsed(c => !c)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: collapsed ? 0 : 10, cursor: "pointer", userSelect: "none" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Garments {selected.size > 0 ? <span style={{ color: "#3b82f6" }}> · {selected.size} selected</span> : null}
          <span style={{ marginLeft: 6, opacity: 0.4 }}>{collapsed ? "▼" : "▲"}</span>
        </div>
        {selected.size > 0 && !collapsed && (
          <button onClick={e => { e.stopPropagation(); onClearAll(); }}
            style={{ fontSize: 11, color: "#ef4444", background: "none", border: "none", cursor: "pointer" }}>
            Clear all
          </button>
        )}
      </div>

      {!collapsed && (<>
      {/* Type filter tabs */}
      <div style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 12, paddingBottom: 4 }}>
        {garmentTypes.map(t => (
          <button key={t} onClick={() => setFilter(t)}
            style={{ padding: "4px 10px", borderRadius: 14, border: "none", fontSize: 11, fontWeight: 600,
                     whiteSpace: "nowrap", cursor: "pointer", flexShrink: 0,
                     background: filter === t ? "#2563eb" : (isDark ? "#1a1f2b" : "#f3f4f6"),
                     color: filter === t ? "#fff" : muted }}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(88px,1fr))", gap: 8 }}>
        {visible.map(g => (
          <GarmentThumb key={g.id} g={g} selected={selected.has(g.id)}
            onClick={() => toggleGarment(g.id)} isDark={isDark} />
        ))}
      </div>
      </>)}
    </div>
  );
}
